/**
 * ResultsMainContent — the 8-section redesigned results page body,
 * rendered between the Header and Footer.
 *
 * Layout follows the client-provided wireframe (`wireframe_results page.pdf`):
 *   1. Hero (black bg, 50/50) with archetype card image
 *   2. Standout answers + top traits with bar graphs (gradient bg)
 *   3. Who shares this archetype (4 tiles: 2 brands + 2 characters)
 *   4. Strengths ✓ / Weaknesses ✗ (2 cols)
 *   5. How to avoid these mistakes + secondary-archetype paywall CTA
 *   6. "This isn't a personality report" + Sample download + "What's in the report"
 *   7. Unlock Your Complete Brand Blueprint — main upsell action box
 *   8. Coupon code section + final complete-blueprint CTA
 *
 * Payment / share / coupon logic stays owned by pages/Results.jsx and is
 * passed in via props. This file is purely presentational.
 */
import React from 'react';
import {
  Mail, Share2, Loader2, Check, X, Lock, Crown, Download,
  Send, Tag, Palette, MessageSquare, Compass, BookOpen,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { getResultContent, getArchetypeCard, getArchetypeExamples } from '../../data/archetypeResultContent';

const PURPLE_CYAN = 'linear-gradient(135deg, #7A00EB 0%, #00c3ef 100%)';
const TEAL = '#02b5b4';

/* --- utilities ---------------------------------------------------------- */

const formatINR = (paise) => `₹${(paise / 100).toLocaleString('en-IN')}`;

/* Section 2: horizontal-bar trait row */
function TraitBar({ name, pct, delay = 0 }) {
  return (
    <div className="flex items-center gap-4 sm:gap-6" data-testid={`trait-bar-${name.toLowerCase()}`}>
      <div className="w-28 sm:w-40 text-sm sm:text-base font-semibold text-white shrink-0">{name}</div>
      <div className="flex-1 h-3 sm:h-3.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{
            width: `${pct}%`,
            background: PURPLE_CYAN,
            animationDelay: `${delay}ms`,
          }}
        />
      </div>
      <div className="w-12 text-right text-sm sm:text-base font-bold text-white/90 tabular-nums">{pct}%</div>
    </div>
  );
}

/* Section 5+7 gradient-outline CTA card */
function GradientOutlineBox({ children, className = '', testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-[2px] ${className}`}
      style={{ background: PURPLE_CYAN }}
    >
      <div className="bg-black rounded-2xl h-full w-full p-6 sm:p-8">{children}</div>
    </div>
  );
}

/* Small chip label */
function ChipLabel({ children }) {
  return (
    <span
      className="inline-block text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase text-white px-3 py-1.5 rounded-full"
      style={{ background: PURPLE_CYAN }}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------------ */

export function ResultsMainContent(props) {
  const {
    results, name, companyName, email, reportPrice, dominant,
    handleEmailResults, handleShare, isSharing,
    handlePremiumPurchase, handleDownloadSample,
    isProcessingPayment, isGeneratingPDF,
    couponCode, setCouponCode, appliedCoupon,
    isApplyingCoupon, handleApplyCoupon, handleRemoveCoupon,
    lastReportId, handleEmailReport, isEmailingReport,
  } = props;

  const content = getResultContent(results.dominant);
  const cardSrc = getArchetypeCard(results.dominant);
  const examples = getArchetypeExamples(results.dominant);

  const currentInr = reportPrice.inr;
  const baseInr = reportPrice.baseInr && reportPrice.baseInr > currentInr ? reportPrice.baseInr : null;
  const payLabel = appliedCoupon
    ? (appliedCoupon.final_amount === 0
        ? 'Get Free Report'
        : `₹${(appliedCoupon.final_amount / 100).toLocaleString('en-IN')}`)
    : `₹${currentInr.toLocaleString('en-IN')}`;

  /* Section 6 discovery boxes — generic, work for any archetype */
  const discoveryBoxes = [
    { icon: Palette,       label: 'Your Visual Identity' },
    { icon: MessageSquare, label: 'Your Tone of Voice' },
    { icon: Compass,       label: 'Your Positioning Direction' },
    { icon: BookOpen,      label: 'Your Custom Moodboard' },
  ];

  /* Section 6 report contents */
  const reportContents = [
    'All about Archetypes',
    'Your Primary & Secondary archetype',
    "Your brand's positioning direction",
    'Visual identity — colours & typography',
    'Tone of voice — dos & don\'ts',
    'Custom moodboard',
    'Brand-building do\'s and don\'ts',
    '16-page personalised PDF blueprint',
  ];

  /* Confidence badge — margin between top-2 weighted scores */
  const confScores = results?.allScores || [];
  const confS1 = confScores[0]?.weightedScore ?? confScores[0]?.count ?? 0;
  const confS2 = confScores[1]?.weightedScore ?? confScores[1]?.count ?? 0;
  const confMargin = confS1 > 0 ? Math.round(((confS1 - confS2) / confS1) * 100) : 0;
  const confLabel = confMargin >= 22 ? 'Decisive Result' : confMargin >= 8 ? 'Strong Match' : 'Blended Profile';

  return (
    <main className="flex-1 bg-black">
      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 1 — HERO                                              */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-black py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 md:gap-10 lg:gap-14 items-center">
          {/* LEFT: text */}
          <div>
            <ChipLabel>Your Brand Archetype</ChipLabel>

            <h1 className="mt-6 sm:mt-8 text-5xl sm:text-6xl md:text-7xl font-thin text-white leading-[0.95] tracking-tight">
              Congratulations{companyName ? `,` : '!'}
              {companyName && (
                <span className="block text-2xl sm:text-3xl md:text-4xl font-light mt-6 sm:mt-8 text-white/70">
                  {companyName}
                </span>
              )}
            </h1>

            <p className="mt-6 sm:mt-8 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
              After analysing your responses, we&apos;ve decoded your brand&apos;s core personality. Your brand is a
            </p>

            <h2
              className="mt-4 sm:mt-6 text-6xl sm:text-7xl md:text-8xl lg:text-[7.5rem] font-black leading-none tracking-tight"
              style={{
                background: PURPLE_CYAN,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
              data-testid="primary-archetype-name"
            >
              {results.dominant}
            </h2>

            {confScores.length >= 2 && (
              <div
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#02b5b4]/40 bg-[#02b5b4]/10 px-4 py-1.5"
                data-testid="confidence-badge"
              >
                <span className="h-2 w-2 rounded-full bg-[#02b5b4]" />
                <span className="text-sm font-semibold text-[#02b5b4] uppercase tracking-wide">{confLabel}</span>
                <span className="text-sm text-white/60">
                  {confMargin >= 8
                    ? `leads by ${confMargin}%`
                    : `closely blended with ${results.secondary}`}
                </span>
              </div>
            )}

            {/* 3-line tagline */}
            <div className="mt-6 sm:mt-8 space-y-1.5 max-w-xl">
              <p className="text-xl sm:text-2xl font-bold text-white">
                {content.tagline.lineBold}
              </p>
              <p className="text-lg sm:text-xl italic font-light text-white/80">
                {content.tagline.lineItalic1}
              </p>
              <p className="text-lg sm:text-xl italic font-light text-white/80">
                {content.tagline.lineItalic2}
              </p>
            </div>

            {/* CTAs */}
            <div className="mt-8 sm:mt-10 flex flex-wrap gap-3 sm:gap-4">
              <Button
                onClick={handleEmailResults}
                size="lg"
                variant="outline"
                className="border-2 border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-6 h-12 sm:h-14"
                data-testid="email-results-btn"
              >
                <Mail className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Email Results
              </Button>
              <Button
                onClick={handleShare}
                disabled={isSharing}
                size="lg"
                variant="outline"
                className="border-2 border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-6 h-12 sm:h-14"
                data-testid="share-results-btn"
              >
                {isSharing
                  ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  : <Share2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />}
                Share Results
              </Button>
            </div>
          </div>

          {/* RIGHT: tilted archetype card */}
          <div className="relative flex justify-center md:justify-end order-first md:order-last">
            <div
              className="relative w-[260px] sm:w-[320px] md:w-[380px] lg:w-[440px] transform transition-transform duration-500 hover:rotate-0"
              style={{
                transform: 'rotate(6deg)',
                filter: 'drop-shadow(0 30px 60px rgba(122,0,235,0.35)) drop-shadow(0 15px 30px rgba(0,195,239,0.25))',
              }}
            >
              <img
                src={cardSrc}
                alt={`${results.dominant} archetype card`}
                className="w-full h-auto object-contain"
                data-testid="archetype-card-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 2 — STANDOUTS & TOP TRAITS  (gradient bg)             */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section
        className="relative py-16 sm:py-20 md:py-24"
        style={{
          background: 'linear-gradient(135deg, #1a0033 0%, #0a0a0a 45%, #002a30 100%)',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-14 sm:space-y-16">
          {/* Standouts */}
          <div>
            <p className="text-xs sm:text-sm font-bold tracking-[0.22em] uppercase mb-6 sm:mb-8" style={{ color: TEAL }}>
              Here&apos;s what stood out about your answers:
            </p>
            <ul className="space-y-3 sm:space-y-4 max-w-3xl">
              {content.stoodOut.map((s, i) => (
                <li key={i} className="flex items-start gap-3 sm:gap-4 text-white/95 text-base sm:text-lg leading-relaxed">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-1" style={{ backgroundColor: TEAL }}>
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Traits */}
          <div>
            <p className="text-xs sm:text-sm font-bold tracking-[0.22em] uppercase mb-6 sm:mb-8" style={{ color: TEAL }}>
              Your Brand&apos;s Top Traits
            </p>
            <div className="space-y-4 sm:space-y-5 max-w-3xl">
              {content.traits.map((t, i) => (
                <TraitBar key={t.name} name={t.name} pct={t.pct} delay={i * 120} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 3 — WHO SHARES THIS ARCHETYPE                         */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="bg-black py-16 sm:py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-light text-white text-center mb-10 sm:mb-14">
            Who shares this archetype?
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {examples.map((ex, i) => (
              <div
                key={i}
                className={`aspect-square bg-white rounded-xl overflow-hidden hover:scale-[1.03] transition-transform duration-300 shadow-lg ${ex.type === 'brand' ? 'flex items-center justify-center p-6 sm:p-8' : ''}`}
                data-testid={`shared-tile-${i}`}
              >
                <img
                  src={ex.src}
                  alt={ex.type === 'brand' ? 'Brand example' : 'Character example'}
                  className={
                    ex.type === 'brand'
                      ? 'max-w-full max-h-full object-contain'
                      : 'w-full h-full object-cover object-top'
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 4 — STRENGTHS & WEAKNESSES                            */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="bg-black py-16 sm:py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6 sm:gap-8">
          {/* Strengths */}
          <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-6 sm:p-8 md:p-10">
            <p className="text-xs sm:text-sm font-bold tracking-[0.22em] uppercase text-emerald-400 mb-6">
              Strengths of your brand
            </p>
            <ul className="space-y-4">
              {content.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-white text-base sm:text-lg">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mt-0.5">
                    <Check className="w-3.5 h-3.5 text-emerald-400" strokeWidth={3} />
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Weaknesses */}
          <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl p-6 sm:p-8 md:p-10">
            <p className="text-xs sm:text-sm font-bold tracking-[0.22em] uppercase text-rose-400 mb-6">
              Weaknesses of your brand
            </p>
            <ul className="space-y-4">
              {content.weaknesses.map((w, i) => (
                <li key={i} className="flex items-start gap-3 text-white text-base sm:text-lg">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-rose-500/15 border border-rose-400/40 flex items-center justify-center mt-0.5">
                    <X className="w-3.5 h-3.5 text-rose-400" strokeWidth={3} />
                  </span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 5 — HOW TO AVOID THESE MISTAKES + Secondary CTA       */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="bg-black py-16 sm:py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-light text-white mb-10 sm:mb-14">
            How to avoid these mistakes
          </h3>
          <div className="grid md:grid-cols-2 gap-8 sm:gap-10 items-center">
            {/* Left column: copy */}
            <div>
              <p className="text-xl sm:text-2xl font-bold text-white leading-snug">
                No great brand has only one personality.
              </p>
              <p className="mt-4 text-lg sm:text-xl italic font-light text-white/80 leading-relaxed">
                Your answers revealed a <span className="not-italic font-bold text-white">Secondary archetype</span> that changes how people experience your brand. It softens, amplifies or balances your primary personality.
              </p>
            </div>

            {/* Right column: gradient-outline pricing card */}
            <GradientOutlineBox testid="secondary-unlock-card">
              <div className="space-y-3 text-left">
                {baseInr && (
                  <p className="text-3xl sm:text-4xl font-bold text-white/50 line-through" data-testid="section5-base-price">
                    ₹{baseInr.toLocaleString('en-IN')}/-
                  </p>
                )}
                <p className="text-sm text-white/70">Download immediately at just</p>
                <p
                  className="text-4xl sm:text-5xl md:text-6xl font-black leading-none"
                  style={{ background: PURPLE_CYAN, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                  data-testid="section5-current-price"
                >
                  ₹{currentInr.toLocaleString('en-IN')}/-
                </p>
                <Button
                  onClick={handlePremiumPurchase}
                  disabled={isProcessingPayment || isGeneratingPDF}
                  className="w-full text-white font-bold h-13 py-4 mt-3 text-base"
                  style={{ background: PURPLE_CYAN }}
                  data-testid="unlock-secondary-btn"
                >
                  {isGeneratingPDF
                    ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Generating…</>
                    : isProcessingPayment
                      ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Processing…</>
                      : <><Lock className="mr-2 h-5 w-5" />Unlock to reveal secondary archetype</>}
                </Button>
              </div>
            </GradientOutlineBox>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 6 — THIS ISN'T A PERSONALITY REPORT                   */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="bg-black py-16 sm:py-20 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl sm:text-4xl md:text-5xl font-light text-white max-w-4xl">
            This isn&apos;t a personality report.
          </h3>
          <p className="mt-4 sm:mt-5 text-lg sm:text-xl italic font-light text-white/80 leading-relaxed max-w-3xl">
            It&apos;s a practical guide for building a brand people instantly recognise.
          </p>

          <div className="mt-12 sm:mt-14 grid md:grid-cols-2 gap-8 sm:gap-10">
            {/* LEFT: You will discover + sample CTA */}
            <div>
              <p className="text-lg sm:text-xl font-bold text-white mb-6">You will discover…</p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {discoveryBoxes.map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="rounded-xl p-[1.5px]"
                    style={{ background: PURPLE_CYAN }}
                  >
                    <div className="bg-black rounded-xl px-4 py-5 sm:py-6 h-full flex flex-col items-center justify-center gap-3 text-center">
                      <Icon className="w-6 h-6 text-white" />
                      <span className="text-sm sm:text-base font-semibold text-white leading-snug">{label}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sample download */}
              <div className="mt-10 text-center sm:text-left">
                <p className="text-sm text-white/60 mb-2">Still in doubt?</p>
                <Button
                  onClick={handleDownloadSample}
                  variant="outline"
                  className="border-2 border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold px-6 h-12"
                  data-testid="download-sample-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download a Sample Report first
                </Button>
                <p className="text-xs text-white/40 mt-2">No sign-up required</p>
              </div>
            </div>

            {/* RIGHT: What's in the report */}
            <div className="rounded-2xl border border-white/15 bg-[#0d0d0d] p-6 sm:p-8">
              <p className="text-xs sm:text-sm font-bold tracking-[0.22em] uppercase mb-5" style={{ color: TEAL }}>
                What&apos;s in the report
              </p>
              <ul className="space-y-3.5">
                {reportContents.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-white text-sm sm:text-base leading-relaxed">
                    <Check className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: TEAL }} strokeWidth={3} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 7 — MAIN UNLOCK CTA (gradient outline action box)     */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="bg-black py-16 sm:py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <GradientOutlineBox testid="main-unlock-card" className="rounded-3xl">
            <div className="text-center py-6 sm:py-10">
              <h3 className="text-4xl sm:text-5xl md:text-6xl font-thin text-white leading-tight tracking-tight max-w-3xl mx-auto">
                Unlock Your Complete Brand Blueprint
              </h3>
              <p className="mt-4 sm:mt-6 italic font-light text-lg sm:text-xl text-white/70 max-w-2xl mx-auto">
                Everything your designer, marketer and copywriter need to build a consistent brand.
              </p>

              {/* Pricing */}
              <div className="mt-8 sm:mt-10 flex flex-col items-center gap-1">
                {baseInr && (
                  <p className="text-3xl sm:text-4xl font-bold text-white/45 line-through" data-testid="section7-base-price">
                    ₹{baseInr.toLocaleString('en-IN')}/-
                  </p>
                )}
                <p className="text-sm text-white/60">Download immediately at just</p>
                <p
                  className="text-5xl sm:text-6xl md:text-7xl font-black leading-none tracking-tight mt-1"
                  style={{ background: PURPLE_CYAN, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                  data-testid="section7-current-price"
                >
                  ₹{currentInr.toLocaleString('en-IN')}/-
                </p>
              </div>

              <Button
                onClick={handlePremiumPurchase}
                disabled={isProcessingPayment || isGeneratingPDF}
                className="mt-8 sm:mt-10 text-white font-bold text-base sm:text-lg h-14 sm:h-16 px-10 sm:px-14"
                style={{ background: PURPLE_CYAN }}
                data-testid="unlock-report-btn"
              >
                {isGeneratingPDF
                  ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" />Generating your report…</>
                  : isProcessingPayment
                    ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" />Processing payment…</>
                    : <><Lock className="mr-2 h-5 w-5" />Unlock Report</>}
              </Button>
            </div>
          </GradientOutlineBox>
        </div>
      </section>

      {/* ────────────────────────────────────────────────────────────── */}
      {/* SECTION 8 — COUPON CODE + FINAL CTA                           */}
      {/* ────────────────────────────────────────────────────────────── */}
      <section className="bg-black pb-20 sm:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Coupon box — dotted outline */}
          <div
            className="rounded-2xl p-6 sm:p-8 relative"
            style={{
              border: '2px dashed rgba(122, 0, 235, 0.55)',
              background: 'rgba(122, 0, 235, 0.04)',
            }}
            data-testid="coupon-section"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: PURPLE_CYAN }}>
                <Tag className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-lg sm:text-xl font-bold text-white">Got a coupon code?</p>
                <p className="text-sm text-white/60 mt-0.5">Apply it below for a discount</p>

                {appliedCoupon ? (
                  <div className="mt-4 flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3">
                    <Check className="h-5 w-5 text-emerald-400 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="font-bold text-emerald-300 text-sm">{couponCode.toUpperCase()}</span>
                      <span className="text-emerald-200 text-sm ml-2">applied — you pay ₹{(appliedCoupon.final_amount / 100).toFixed(0)}</span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-rose-400 hover:text-rose-300 text-sm font-medium underline"
                      data-testid="remove-coupon-btn"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
                    <Input
                      type="text"
                      placeholder="Enter code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="flex-1 h-12 text-sm uppercase tracking-wider font-mono bg-black border-white/20 text-white placeholder:text-white/30 focus:border-purple-400"
                      data-testid="coupon-input"
                    />
                    <Button
                      type="button"
                      onClick={handleApplyCoupon}
                      disabled={isApplyingCoupon || !couponCode.trim()}
                      className="h-12 px-8 font-bold text-white"
                      style={{ background: PURPLE_CYAN }}
                      data-testid="apply-coupon-btn"
                    >
                      {isApplyingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Final CTA with pricing block */}
          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="flex flex-col items-center gap-1 mb-2">
              {baseInr && (
                <p className="text-3xl sm:text-4xl font-bold text-white/45 line-through" data-testid="section8-base-price">
                  ₹{baseInr.toLocaleString('en-IN')}/-
                </p>
              )}
              <p className="text-sm text-white/60">Download immediately at just</p>
              <p
                className="text-4xl sm:text-5xl md:text-6xl font-black leading-none tracking-tight mt-1"
                style={{ background: PURPLE_CYAN, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                data-testid="section8-current-price"
              >
                {appliedCoupon
                  ? `₹${(appliedCoupon.final_amount / 100).toLocaleString('en-IN')}/-`
                  : `₹${currentInr.toLocaleString('en-IN')}/-`}
              </p>
            </div>

            <Button
              onClick={handlePremiumPurchase}
              disabled={isProcessingPayment || isGeneratingPDF}
              className="w-full sm:max-w-xl text-white font-bold text-base sm:text-lg h-14 sm:h-16"
              style={{ background: PURPLE_CYAN }}
              data-testid="final-blueprint-btn"
            >
              {isGeneratingPDF
                ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" />Generating Your Report…</>
                : isProcessingPayment
                  ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" />Processing Payment…</>
                  : <><Crown className="mr-2 h-5 w-5" />Show me my Brand&apos;s complete blueprint</>}
            </Button>

            {/* Email report button (only visible after a report was generated in this session) */}
            {lastReportId && (
              <Button
                onClick={() => handleEmailReport()}
                disabled={isEmailingReport}
                variant="outline"
                className="border-white/25 text-white hover:bg-white/10 hover:text-white font-medium"
                data-testid="email-report-btn"
              >
                {isEmailingReport
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>
                  : <><Send className="mr-2 h-4 w-4" />Email report to {email}</>}
              </Button>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

export default ResultsMainContent;
